import React, { useEffect, useRef, useState } from "react";

import styles from "./Filter.module.css";

import SideModal from "common-ui-components/components/side-modal/SideModal";
import TableControlButton from "../TableControlButton";
import filterIcon from "../../../icons/filter.svg";
import { useTableManagerContext } from "../../TableManagerContext";
import QuickFilters from "./QuickFilters";
import FilterContainer from "./FilterContainer";

const buildInitialFilters = (filters = []) => {
  return filters.reduce((acc, { column, type, options = [] }) => {
    const initialOptions = options.filter((option) => option.initially_active);
    if (initialOptions.length === 0) return acc;

    if (type === "multiple") {
      acc.push({
        column,
        filterValue: initialOptions.map((option) => option.value),
      });
      return acc;
    }

    acc.push({ column, filterValue: initialOptions[0].value });
    return acc;
  }, []);
};

const Filter = ({ handleAction, filterComponents = [] }) => {
  const [filtersForSaving, setFiltersForSaving] = useState({
    quick_filters: [],
    comprehensive_filters: [],
    dynamic_filters: [],
  });

  const { tableControls, setTableControls, filterConfig, filter, setFilter } = useTableManagerContext();

  //stores comprehensive filters selected while filter is in open
  const [comprehensiveFilter, setComprehensiveFilter] = useState([]);
  //stores comprehensive filters which are applied even in filter closed
  const [appliedFilters, setAppliedFilters] = useState([]);

  //stores dynamic filters selected while filter is in open
  const [dynamicFilter, setDynamicFilter] = useState([]);
  //stores dynamic filters which are applied even in filter closed
  const [appliedDynamicFilters, setAppliedDynamicFilters] = useState([]);

  //stores applied custom ui filters
  const [appliedCustomFilter, setAppliedCustomFilter] = useState([]);

  //controls filter closure and sets filters from the applied ones on opening
  const setFilterOpen = (openStatus) => {
    if (openStatus) 
    {
      setComprehensiveFilter(appliedFilters);
      setDynamicFilter(appliedDynamicFilters);
    }
    setTableControls((prevTableControls) => ({
      ...prevTableControls,
      filter: openStatus,
    }));
  };

  //getting filter config from the contxt - props passsed to table manager or from backend
  const { quick_filters, comprehensive_filters, dynamic_filters } = filterConfig || {};

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || !comprehensive_filters?.length) 
      return;

    const comprehensiveColumns = new Set(comprehensive_filters.map((config) => config.column));
    const existingComprehensiveFilters = filter.filter((f) =>
      comprehensiveColumns.has(f.column),
    );

    // Respect already provided filter state and avoid overriding it.
    if (existingComprehensiveFilters.length > 0) {
      setComprehensiveFilter(existingComprehensiveFilters);
      setAppliedFilters(existingComprehensiveFilters);
      initializedRef.current = true;
      return;
    }

    const initialComprehensiveFilters = buildInitialFilters(comprehensive_filters,);
    if (initialComprehensiveFilters.length > 0) 
    {
      const nonComprehensiveFilters = filter.filter(
        (f) => !comprehensiveColumns.has(f.column),
      );
      const nextFilters = nonComprehensiveFilters.concat(
        initialComprehensiveFilters,
      );

      setFilter(nextFilters);
      setComprehensiveFilter(initialComprehensiveFilters);
      setAppliedFilters(initialComprehensiveFilters);
    }

    initializedRef.current = true;
  }, [comprehensive_filters, filter, setFilter]);

  useEffect(() => {
    if (!initializedRef.current || !comprehensive_filters?.length) return;
    if (tableControls.filter) return;

    const comprehensiveColumns = new Set(
      comprehensive_filters.map((config) => config.column),
    );
    const existingComprehensiveFilters = filter.filter((f) =>
      comprehensiveColumns.has(f.column),
    );

    setComprehensiveFilter(existingComprehensiveFilters);
    setAppliedFilters(existingComprehensiveFilters);
  }, [comprehensive_filters, filter, tableControls.filter]);

  useEffect(() => {
    if (!initializedRef.current || !dynamic_filters?.length) return;

    const dynamicColumns = new Set(
      dynamic_filters.map((config) => config.column),
    );

    const existingDynamicFilters = filter.filter((f) =>
      dynamicColumns.has(f.column),
    );

    if (existingDynamicFilters.length > 0) {
      setDynamicFilter(existingDynamicFilters);
      setAppliedDynamicFilters(existingDynamicFilters);
      return;
    }

    const initialDynamicFilters = buildInitialFilters(dynamic_filters);

    if (initialDynamicFilters.length > 0) {
      const nonDynamicFilters = filter.filter(
        (f) => !dynamicColumns.has(f.column),
      );

      const nextFilters = nonDynamicFilters.concat(initialDynamicFilters);

      setFilter(nextFilters);
      setDynamicFilter(initialDynamicFilters);
      setAppliedDynamicFilters(initialDynamicFilters);
    }
  }, [dynamic_filters, filter, setFilter]);

  useEffect(() => {
    if (!dynamic_filters?.length) return;
    if (tableControls.filter) return;

    const dynamicColumns = new Set(
      dynamic_filters.map((config) => config.column),
    );

    const existingDynamicFilters = filter.filter((f) =>
      dynamicColumns.has(f.column),
    );

    setDynamicFilter(existingDynamicFilters);
    setAppliedDynamicFilters(existingDynamicFilters);
  }, [dynamic_filters, filter, tableControls.filter]);

  useEffect(() => {
    const customColumns = new Set(filterComponents.map(({ column }) => column));
    const existingCustomFilters = filter.filter((item) =>
      customColumns.has(item.column),
    );

    setAppliedCustomFilter((previousFilters) => {
      const unchanged =
        previousFilters.length === existingCustomFilters.length &&
        previousFilters.every(
          (previousFilter, index) =>
            previousFilter.column === existingCustomFilters[index]?.column &&
            JSON.stringify(previousFilter.filterValue) ===
              JSON.stringify(existingCustomFilters[index]?.filterValue),
        );

      return unchanged ? previousFilters : existingCustomFilters;
    });
  }, [filter, filterComponents]);

  const testing = () => {
    //console.log("filters to be saved--------", filtersForSaving);
  };

  if (!filterConfig && filterComponents.length === 0) return null;

  const configuredQuickFilters = quick_filters || [];
  const configuredComprehensiveFilters = comprehensive_filters || [];
  const configuredDynamicFilters = dynamic_filters || [];

  return (
    <div className={styles.Filter}>
      {/* <div onClick={()=>testing()}>T</div> */}
      {configuredQuickFilters.length > 0 && (
        <QuickFilters
          filterConfig={configuredQuickFilters}
          setFiltersForSaving={setFiltersForSaving}
        />
      )}
      {(configuredComprehensiveFilters.length > 0 ||
        configuredDynamicFilters.length > 0 ||
        filterComponents.length > 0) && (
        <SideModal
          open={tableControls.filter}
          setOpen={setFilterOpen}
          trigger={
            <TableControlButton
              dot={
                appliedFilters.length !== 0 ||
                appliedDynamicFilters.length !== 0 ||
                appliedCustomFilter.length !== 0
              }
              name="Filter"
              icon={filterIcon}
              control="filter"
              handleAction={(...args) => {
                setFilterOpen(true);
                handleAction(args);
              }}
            />
          }
        >
          <FilterContainer
            setFilterOpen={setFilterOpen}

            filterConfig={configuredComprehensiveFilters}
            comprehensiveFilter={comprehensiveFilter}
            setComprehensiveFilter={setComprehensiveFilter}
            setAppliedFilters={setAppliedFilters} //for comprehnsive only

            dynamicFilterConfig={configuredDynamicFilters}
            dynamicFilter={dynamicFilter}
            setDynamicFilter={setDynamicFilter}
            setAppliedDynamicFilters={setAppliedDynamicFilters}

            setAppliedCustomFilter={setAppliedCustomFilter}

            filtersForSaving={filtersForSaving}
            setFiltersForSaving={setFiltersForSaving}

            filterComponents={filterComponents}
          />
        </SideModal>
      )}
    </div>
  );
};

export default Filter;
